import { colors } from '@kmns-internal/furikake'
import { DeleteOutlined, MoreHoriz } from '@mui/icons-material'
import {
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import type { ApprovalFlow } from '@ulysses-inc/harami_api_client'
import type React from 'react'
import { type MouseEvent, useState } from 'react'

interface OwnProps {
  approvalFlows: ApprovalFlow[]
  onEdit: (approvalFlowId: string) => void
  onDelete: (approvalFlowId: string) => void
}

interface RowData {
  uuid: string
  name?: string
}

const ApprovalFlowList: React.FC<OwnProps> = (props: OwnProps) => {
  // 3点リーダーメニュー
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedRow, setSelectedRow] = useState<RowData | null>(null)
  const handleMenuClick = (event: MouseEvent<HTMLElement>, row: RowData) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
    setSelectedRow(row)
  }
  const handleMenuClose = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setAnchorEl(null)
  }

  // 削除ダイアログ
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const handleMenuDelete = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setOpenDeleteDialog(true)
  }
  const handleCloseDeleteDialog = (event: MouseEvent<HTMLElement>) => {
    setOpenDeleteDialog(false)
    handleMenuClose(event)
  }
  const handleDelete = (
    event: MouseEvent<HTMLElement>,
    selectedRow: RowData | null,
  ) => {
    if (selectedRow?.uuid !== undefined) {
      props.onDelete(selectedRow.uuid)
    }
    setOpenDeleteDialog(false)
    handleMenuClose(event)
  }

  return (
    <>
      <Table sx={{ width: '100%' }} stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell
              size="small"
              sx={{
                whiteSpace: 'normal',
                width: '35%',
              }}
            >
              承認フロー名
            </TableCell>
            <TableCell
              size="small"
              sx={{
                whiteSpace: 'normal',
                width: '65%',
              }}
            >
              説明
            </TableCell>
            <TableCell
              size="small"
              sx={{
                whiteSpace: 'normal',
                width: '60px',
              }}
            />
          </TableRow>
        </TableHead>
        <TableBody sx={{ backgroundColor: 'white' }}>
          {props.approvalFlows.map(
            (approvalFlow: ApprovalFlow, index: number) => (
              <TableRow
                key={approvalFlow.uuid}
                onClick={() => props.onEdit(approvalFlow.uuid ?? '')}
                data-testid={`approval-flow-row-${index}`}
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: colors.action.hover,
                  },
                }}
              >
                <TableCell
                  size="small"
                  sx={{
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                  }}
                >
                  <Typography
                    variant="body2"
                    data-testid={`approval-flow-name-${index}`}
                  >
                    {approvalFlow.name}
                  </Typography>
                </TableCell>
                <TableCell
                  size="small"
                  sx={{
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                  }}
                >
                  <Typography
                    variant="body2"
                    data-testid={`approval-flow-description-${index}`}
                  >
                    {approvalFlow.description}
                  </Typography>
                </TableCell>
                <TableCell size="small" align="right" sx={{ minWidth: '60px' }}>
                  <IconButton
                    onClick={e => {
                      handleMenuClick(e, {
                        uuid: approvalFlow.uuid ?? '',
                        name: approvalFlow.name,
                      })
                    }}
                    data-testid={`approval-flow-menu-button-${index}`}
                    aria-label="承認フローのメニューを開く"
                  >
                    <MoreHoriz />
                  </IconButton>
                </TableCell>
              </TableRow>
            ),
          )}
        </TableBody>
      </Table>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        data-testid="approval-flow-menu"
      >
        <MenuItem
          onClick={handleMenuDelete}
          data-testid="approval-flow-delete-menu-item"
          sx={{
            color: colors.error.main,
            '&:hover': {
              backgroundColor: colors.error.hover,
              color: colors.error.main,
            },
            gap: 1,
          }}
        >
          <DeleteOutlined />
          削除
        </MenuItem>
      </Menu>
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        data-testid="approval-flow-delete-dialog"
      >
        <DialogTitle data-testid="approval-flow-delete-dialog-title">
          {`「${selectedRow?.name}」の削除を実行しますがよろしいですか？`}
        </DialogTitle>
        <DialogActions>
          <Button
            color="secondary"
            variant="contained"
            onClick={handleCloseDeleteDialog}
            data-testid="approval-flow-delete-cancel-button"
          >
            キャンセル
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={e => handleDelete(e, selectedRow)}
            data-testid="approval-flow-delete-confirm-button"
          >
            削除
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default ApprovalFlowList
